import {
  AuthoringDestinationGroup,
  AuthoringProperty,
  MetroPropertyItem,
} from '../../molecules/SearchField/StaticHotelSearchField/StaticHotelSearchField.types';
/** Normalize strings (trim, collapse spaces, lowercase). */
export const norm = (s?: unknown) =>
  (typeof s === 'string' ? s : String(s ?? ''))?.trim()?.replace(/\s+/g, ' ')?.toLowerCase();

/** Normalize codes (trim + uppercase for stable identity). */
export const normCode = (s?: string) => (s ?? '')?.trim()?.toUpperCase();

/** Is marsha authored (non-empty after normalize)? */
export const hasValidMarsha = (property: { marshaCode?: string }) => normCode(property?.marshaCode)?.length > 0;
/**
 * Grouped identity: keep same-name hotels separate by metro when not typing.
 * Prefer marsha; fallback to name+metro (but we already drop items with no marsha).
 */
export const keyGroup = (property: { marshaCode?: string; propertyName: string }, metroArea: string) => {
  const m = normCode(property?.marshaCode);
  return m ? `m:${m}` : `n:${norm(property?.propertyName)}::metro:${norm(metroArea)}`;
};

/**
 * Suggestions identity: MERGED LIST while typing.
 * Collapse duplicates across metros by NAME-ONLY (ignore marsha and metro).
 */
export const keySuggestionNameOnly = (property: { propertyName: string }) => `n:${norm(property?.propertyName)}`;

/** Generic dedupe helper using a key function. */
export const dedupeByKey = <T>(items: T[], keyFn: (it: T) => string): Map<string, T> => {
  const seen = new Map<string, T>();
  for (const it of items) {
    const key = keyFn(it);
    if (!seen?.has(key)) {
      seen?.set(key, it);
    }
  }
  return seen;
};

/**
 * Merge duplicate metros (case/whitespace-insensitive) and dedupe properties inside each metro.
 * IMPORTANT: Drop any property with NO valid marshaCode at ingress.
 */
export const normalizeGroups = (groups: AuthoringDestinationGroup[]): AuthoringDestinationGroup[] => {
  const byMetro = new Map<string, { metroArea: string; properties: AuthoringProperty[] }>();
  for (const g of groups ?? []) {
    const metroDisplay = (g?.metroArea ?? '').trim();
    const metroKey = norm(metroDisplay);
    // Filter out "no marsha" properties immediately
    const incomingAll = Array.isArray(g?.properties) ? g?.properties : [];
    const incoming = incomingAll?.filter(hasValidMarsha);
    const bucket = byMetro?.get(metroKey);
    if (!bucket) {
      byMetro?.set(metroKey, { metroArea: metroDisplay, properties: [...incoming] });
    } else {
      bucket?.properties?.push(...incoming);
    }
  }

  const merged: AuthoringDestinationGroup[] = [];
  for (const { metroArea, properties } of byMetro.values()) {
    // Dedupe inside each metro with grouped identity (name+metro fallback, but all have marsha)
    const groupedMap = dedupeByKey(properties, property => keyGroup(property, metroArea));
    merged?.push({ metroArea, properties: Array.from(groupedMap?.values()) });
  }
  return merged;
};

/** Flatten authoring groups to a single list of properties with metroArea attached. */
export const flattenProperties = (groups: AuthoringDestinationGroup[]): MetroPropertyItem[] =>
  groups?.flatMap(g => (g?.properties ?? [])?.map(property => ({ ...property, metroArea: g?.metroArea })));

/** Filter flat properties by propertyName, marshaCode, or searchKeyword. (All items here have valid marsha.) */
export const filterByPropertyText = (items: MetroPropertyItem[], query: string): MetroPropertyItem[] => {
  const qNorm = norm(query);
  const qCode = normCode(query);
  if (!qNorm && !qCode) return [];
  return items?.filter(
    i =>
      norm(i?.propertyName)?.includes(qNorm) ||
      normCode(i?.marshaCode)?.includes(qCode) ||
      norm(i?.searchKeyword)?.includes(qNorm)
  );
};

/** Filter by metro name; if a metro matches, include all its properties. */
export const filterByMetroText = (groups: AuthoringDestinationGroup[], query: string): MetroPropertyItem[] => {
  const q = norm(query);
  if (!q) return [];
  const matched = groups?.filter(g => norm(g?.metroArea)?.includes(q));
  return flattenProperties(matched);
};

/**
 * When deduping suggestions by name-only, multiple items with the same name can exist (across metros).
 * We choose ONE representative deterministically:
 * 1) Prefer the item whose metro name contains the query substring.
 * 2) Else prefer by metroArea alphabetical.
 * 3) Then by marshaCode alphabetical (all items have marsha).
 */
export const chooseRepresentativeByQuery = (candidates: MetroPropertyItem[], query: string): MetroPropertyItem => {
  const q = norm(query);
  const score = (it: MetroPropertyItem) => {
    let s = 0;
    if (q && norm(it?.metroArea)?.includes(q)) s += 2; // prefer matching metro
    if (q && norm(it?.propertyName)?.includes(q)) s += 1; // true after filter, but explicit
    return s;
  };

  return candidates.slice().sort((a, b) => {
    const sa = score(a);
    const sb = score(b);
    if (sa !== sb) return sb - sa;
    const metroCmp = norm(a?.metroArea)?.localeCompare(norm(b?.metroArea));
    if (metroCmp !== 0) return metroCmp;
    return normCode(a?.marshaCode)?.localeCompare(normCode(b?.marshaCode));
  })[0];
};

/**
 * Build merged suggestions with name-only dedupe across metros.
 * Items with NO marshaCode never reach here (filtered at ingress).
 */
export const buildSuggestionListNoDup = (
  allFlat: MetroPropertyItem[],
  groups: AuthoringDestinationGroup[],
  query: string
): MetroPropertyItem[] => {
  const byProperty = filterByPropertyText(allFlat, query);
  const byMetro = filterByMetroText(groups, query);

  // Combine both arrays
  const combined = [...byProperty, ...byMetro];

  // Bucket by name-only key
  const buckets = new Map<string, MetroPropertyItem[]>();
  for (const item of combined) {
    const key = keySuggestionNameOnly(item);
    const list = buckets?.get(key);
    if (list) list?.push(item);
    else buckets?.set(key, [item]);
  }

  // For each bucket (same property name), choose one representative
  const result: MetroPropertyItem[] = [];
  for (const list of buckets.values()) {
    result?.push(chooseRepresentativeByQuery(list, query));
  }

  // Stable ordering: by propertyName, then metro
  return result?.sort((a, b) => {
    const nameCmp = norm(a?.propertyName)?.localeCompare(norm(b?.propertyName));
    if (nameCmp !== 0) return nameCmp;
    return norm(a?.metroArea)?.localeCompare(norm(b?.metroArea));
  });
};
